import { type GetSearchOptions, getSearch } from '@/core/data/gateways/api';
import { transformWithFactory } from '@/core/data/transformers/transformer';
import { compactMap } from '@/core/utils/array';
import type { City } from '@/events/cities/city.schema';
import { createEvent } from '@/events/events/event.factory';
import type { Event } from '@/events/events/event.schema';
import { createPerformer } from '@/events/performers/performer.factory';
import type { Performer } from '@/events/performers/performer.schema';
import { createVenue } from '@/events/venues/venue.factory';
import type { Venue } from '@/events/venues/venue.schema';
import type { GetSearchResultsResponse, SearchResults } from './search.schema';

type Maximums = Partial<Record<'events' | 'performers' | 'venues', number>>;

const DEFAULT_MAX_SEARCH_RESULTS = 4;

function transformSearchResults(
	raw: GetSearchResultsResponse,
	max?: Maximums,
): [
	['events', Promise<Event[]>],
	['performers', Promise<Performer[]>],
	['venues', Promise<Venue[]>],
] {
	return [
		[
			'events',
			raw.events === undefined
				? Promise.resolve([])
				: transformWithFactory(
						raw.events.slice(0, max?.events ?? DEFAULT_MAX_SEARCH_RESULTS),
						createEvent,
					),
		],
		[
			'performers',
			raw.performers === undefined
				? Promise.resolve([])
				: transformWithFactory(
						raw.performers.slice(
							0,
							max?.performers ?? DEFAULT_MAX_SEARCH_RESULTS,
						),
						createPerformer,
					),
		],
		[
			'venues',
			raw.venues === undefined
				? Promise.resolve([])
				: transformWithFactory(
						raw.venues.slice(0, max?.venues ?? DEFAULT_MAX_SEARCH_RESULTS),
						createVenue,
					),
		],
	];
}

export async function getSearchResults(
	query: string,
	city?: City,
	limits?: Maximums,
): Promise<SearchResults> {
	const options: GetSearchOptions = { q: query };

	if (city) {
		options.lat = city.location.coordinates.lat;
		options.long = city.location.coordinates.lon;
	}

	const search = await getSearch(options);

	if (!search) return { events: [], performers: [], venues: [] };

	const mapped = transformSearchResults(search, limits);

	const response = compactMap(
		await Promise.allSettled(
			mapped.map(async ([key, value]) => [key, await value]),
		),
		(item) => {
			if (item.status !== 'fulfilled') return null;

			return item.value;
		},
	);

	return Object.fromEntries(response);
}
